import { booleanValid, featureCollection, intersect } from '@turf/turf';
import type { Feature, MultiPolygon, Polygon } from 'geojson';

export type SafeIntersectGeometry = Polygon | MultiPolygon;

type LabeledFeature = { label: string; feature: Feature<SafeIntersectGeometry> };

export const cgIntersect = (a: LabeledFeature, b: LabeledFeature): Feature<SafeIntersectGeometry> | null => {
  try {
    return intersect(featureCollection([a.feature, b.feature]));
  } catch (err) {
    const invalid = [a, b].filter(({ feature }) => !booleanValid(feature));

    let reason: string;
    if (invalid.length === 1) {
      const [broken] = invalid;
      const other = broken === a ? b : a;
      reason = `${broken.label} has invalid geometry (intersecting with ${other.label})`;
    } else if (invalid.length === 2) {
      reason = `both ${a.label} and ${b.label} have invalid geometry`;
    } else {
      reason = `intersecting ${a.label} and ${b.label} (neither flagged invalid by booleanValid)`;
    }

    const originalMessage = err instanceof Error ? err.message : String(err);
    throw new Error(`cgIntersect failed: ${reason}: ${originalMessage}`, { cause: err });
  }
};

/**
 * Wraps cgIntersect and falls back to null instead of throwing — for call sites that shouldn't
 * hard-fail on one bad overlap (e.g. skipping a single grid cell in a tiling/report pipeline).
 * Logs the swallowed error so these failures still surface in Datadog even though execution continues.
 */
export const cgIntersectOrNull = (
  a: LabeledFeature,
  b: LabeledFeature,
  onError: (err: unknown, message: string) => void,
): Feature<SafeIntersectGeometry> | null => {
  try {
    return cgIntersect(a, b);
  } catch (err) {
    // err.message is cgIntersect's own diagnostic (which of a/b is the broken operand) — reuse it
    // as the log message so a swallowed failure has the same detail as an unswallowed one.
    onError(err, err instanceof Error ? err.message : String(err));
    return null;
  }
};
